import { forwardRef, useEffect, useRef } from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Checkbox — native input + custom visual (Faza 8 polish).
 *
 * Features:
 *   - 3 size: sm (14) | md (16) | lg (20)
 *   - indeterminate prop (yarim-tanlangan, table header bulk-select uchun)
 *   - checked emerald (success), indeterminate violet
 *   - keyboard: Space toggle (native)
 *   - focus ring violet
 *
 * Token'lar: --checkbox-* (tokens.css)
 */
type CheckboxSize = "sm" | "md" | "lg";

const SIZE_PX: Record<CheckboxSize, { box: string; icon: number }> = {
  sm: { box: "h-3.5 w-3.5", icon: 10 },
  md: { box: "h-4 w-4", icon: 12 },
  lg: { box: "h-5 w-5", icon: 14 },
};

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
  size?: CheckboxSize;
  /** Yarim-tanlangan holat (header bulk-select) */
  indeterminate?: boolean;
  /** Custom label (right side) — ixtiyoriy */
  label?: React.ReactNode;
  /** Helper sub-text (label ostida) */
  description?: React.ReactNode;
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") ref(node);
      else if (ref && typeof ref === "object")
        (ref as React.MutableRefObject<T | null>).current = node;
    });
  };
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      size = "md",
      indeterminate = false,
      label,
      description,
      checked,
      disabled,
      id,
      ...props
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLInputElement | null>(null);
    const cfg = SIZE_PX[size];

    useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    const visual = (
      <span
        aria-hidden
        className={cn(
          "relative inline-flex shrink-0 items-center justify-center rounded-[5px] border transition-all duration-150",
          cfg.box,
          // Default
          "bg-[var(--checkbox-bg)] border-[var(--checkbox-border)]",
          "peer-hover:border-[var(--checkbox-border-hover)]",
          // Checked (emerald)
          "peer-checked:bg-[var(--checkbox-checked-bg)] peer-checked:border-[var(--checkbox-checked-border)]",
          // Indeterminate (violet)
          indeterminate &&
            "!bg-[var(--checkbox-indeterminate-bg)] !border-[var(--checkbox-indeterminate-border)]",
          // Focus ring
          "peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--accent-violet)]/50 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[var(--bg-base)]",
          // Disabled
          "peer-disabled:opacity-50",
        )}
      >
        {indeterminate ? (
          <Minus
            size={cfg.icon}
            strokeWidth={3.5}
            className="text-white"
          />
        ) : (
          <Check
            size={cfg.icon}
            strokeWidth={3.5}
            className={cn(
              "text-[var(--checkbox-checked-fg)] opacity-0 transition-opacity",
              "peer-checked:opacity-100",
            )}
          />
        )}
      </span>
    );

    const input = (
      <input
        ref={mergeRefs(innerRef, ref)}
        type="checkbox"
        id={id}
        checked={checked}
        disabled={disabled}
        className="peer sr-only"
        {...props}
      />
    );

    if (!label && !description) {
      return (
        <span className={cn("inline-flex items-center", className)}>
          {input}
          {visual}
        </span>
      );
    }

    return (
      <label
        htmlFor={id}
        className={cn(
          "group inline-flex items-start gap-2.5 cursor-pointer select-none",
          disabled && "cursor-not-allowed opacity-60",
          className,
        )}
      >
        {input}
        <span className="mt-0.5">{visual}</span>
        <span className="flex flex-col gap-0.5">
          {label && (
            <span className="text-sm font-medium text-[var(--text-primary)] leading-tight">
              {label}
            </span>
          )}
          {description && (
            <span className="text-xs text-[var(--text-secondary)] leading-snug">
              {description}
            </span>
          )}
        </span>
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";
